import { isPotentialMarkdownTable } from "./markdownTables.ts";

export interface CompletedRevealBlock {
  text: string;
  sourceEnd: number;
}

export interface DisplayedRevealBlock {
  id: number;
  text: string;
  isLive: boolean;
}

export interface StreamingRevealState {
  source: string;
  committedSourceLength: number;
  blocks: DisplayedRevealBlock[];
  nextBlockId: number;
  generation: number;
}

type CompletedRevealBlockParser = (
  content: string,
  holdTrailingTableCandidate: boolean
) => CompletedRevealBlock[];

export function createStreamingRevealState(): StreamingRevealState {
  return {
    source: "",
    committedSourceLength: 0,
    blocks: [],
    nextBlockId: 0,
    generation: 0,
  };
}

function withoutLiveBlock(blocks: DisplayedRevealBlock[]): DisplayedRevealBlock[] {
  const lastBlock = blocks[blocks.length - 1];
  return lastBlock?.isLive ? blocks.slice(0, -1) : blocks;
}

function getLiveBlock(
  blocks: DisplayedRevealBlock[]
): DisplayedRevealBlock | undefined {
  const lastBlock = blocks[blocks.length - 1];
  return lastBlock?.isLive ? lastBlock : undefined;
}

function resetForContent(
  state: StreamingRevealState,
  content: string
): StreamingRevealState {
  return {
    source: content,
    committedSourceLength: 0,
    blocks: [],
    nextBlockId: 0,
    generation: state.generation + 1,
  };
}

/**
 * Reconciles newly accumulated stream content with the reveal blocks.
 *
 * An idle block remains live until it reaches a real Markdown boundary or the
 * stream ends. New token chunks therefore update that block in place instead
 * of becoming separate visual blocks at arbitrary network boundaries.
 */
export function reconcileStreamingReveal(
  previousState: StreamingRevealState,
  content: string,
  isStreaming: boolean,
  parseCompletedBlocks: CompletedRevealBlockParser
): StreamingRevealState {
  let state = content.startsWith(previousState.source)
    ? { ...previousState, source: content }
    : resetForContent(previousState, content);
  let pendingContent = content.slice(state.committedSourceLength);
  const completedBlocks = parseCompletedBlocks(pendingContent, isStreaming);

  if (completedBlocks.length > 0) {
    const consumedSourceLength = completedBlocks.reduce(
      (maxSourceEnd, block) => Math.max(maxSourceEnd, block.sourceEnd),
      0
    );
    const liveBlock = getLiveBlock(state.blocks);
    let nextBlockId = state.nextBlockId;
    const promotedBlocks = completedBlocks
      .filter((block) => block.text.trim() !== "")
      .map((block, index) => ({
        id: index === 0 && liveBlock ? liveBlock.id : nextBlockId++,
        text: block.text,
        isLive: false,
      }));
    const committedSourceLength =
      state.committedSourceLength + consumedSourceLength;
    const remainingContent = content.slice(committedSourceLength);
    const blocks = [...withoutLiveBlock(state.blocks), ...promotedBlocks];

    // If the user has already seen the live tail, keep any remainder visible
    // while promoting the completed prefix. This prevents text from flashing
    // away when a paragraph or fenced block becomes complete.
    if (liveBlock && remainingContent.trim()) {
      blocks.push({
        id: nextBlockId++,
        text: remainingContent.trim(),
        isLive: true,
      });
    }

    state = {
      ...state,
      committedSourceLength,
      blocks,
      nextBlockId,
    };
    pendingContent = remainingContent;
  } else {
    const liveBlock = getLiveBlock(state.blocks);

    if (liveBlock) {
      const blocks = withoutLiveBlock(state.blocks);
      if (pendingContent.trim()) {
        blocks.push({ ...liveBlock, text: pendingContent.trim() });
      }
      state = { ...state, blocks };
    }
  }

  if (!isStreaming && pendingContent.trim()) {
    const liveBlock = getLiveBlock(state.blocks);
    let nextBlockId = state.nextBlockId;
    const finalBlock: DisplayedRevealBlock = {
      id: liveBlock?.id ?? nextBlockId++,
      text: pendingContent.trim(),
      isLive: false,
    };

    state = {
      ...state,
      committedSourceLength: content.length,
      blocks: [...withoutLiveBlock(state.blocks), finalBlock],
      nextBlockId,
    };
  }

  return state;
}

export function revealIdleTail(
  state: StreamingRevealState,
  content: string
): StreamingRevealState {
  // Ignore stale timers and do not create a second live block.
  if (content !== state.source || getLiveBlock(state.blocks)) {
    return state;
  }

  const pendingContent = content.slice(state.committedSourceLength);
  if (!pendingContent.trim() || isPotentialMarkdownTable(pendingContent)) {
    return state;
  }

  return {
    ...state,
    blocks: [
      ...state.blocks,
      {
        id: state.nextBlockId,
        text: pendingContent.trim(),
        isLive: true,
      },
    ],
    nextBlockId: state.nextBlockId + 1,
  };
}
